import { useState, useCallback } from 'react';

/**
 * Custom hook for displaying toast notifications
 * Provides a simple API for success, error, info, and warning toasts
 *
 * Usage:
 * const { showToast, toasts, dismissToast } = useCustomToast();
 * showToast({ type: 'success', message: 'Operation successful!' });
 */
export const useCustomToast = () => {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(({ type = 'info', message, duration = 5000 }) => {
    const id = Date.now() + Math.random();
    const toast = { id, type, message, duration };

    setToasts((prev) => [...prev, toast]);

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        dismissToast(id);
      }, duration);
    }

    return id;
  }, [dismissToast]);

  const success = useCallback((message, duration) => {
    return showToast({ type: 'success', message, duration });
  }, [showToast]);

  const error = useCallback((message, duration) => {
    return showToast({ type: 'error', message, duration });
  }, [showToast]);

  const info = useCallback((message, duration) => {
    return showToast({ type: 'info', message, duration });
  }, [showToast]);

  const warning = useCallback((message, duration) => {
    return showToast({ type: 'warning', message, duration });
  }, [showToast]);

  return {
    toasts,
    showToast,
    dismissToast,
    success,
    error,
    info,
    warning,
  };
};

export default useCustomToast;
